//------------------------------------------------------------------------------
// BFA10104G 共架申込 通信設備
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { CellType } from '@grapecity/wijmo.grid';
import { GridItems } from '@/utils/grid-items';
import CustomMultiRow from '@/components/molecules/CustomMultiRow';

// 属性定数
const READONLY = { isReadOnly: true };  // 編集不可
const REQUIRED = { isRequired: true };  // 必須
const KKN_RIKAKU = "径間途中の離隔\n";

// 径間一覧項目
const GRID_COLUMNS = new GridItems()

  // 電柱(1)
  .group('電柱')
    .text('k1areaName',  100, '地区(1)',     READONLY)
    .text('k1lineName',  100, '冠称(1)',     READONLY)
    .text('k1poleNo',    120, '電柱番号(1)', READONLY)
    .text('k1poleKind',  100, '施設名(1)',   READONLY)
    .end()

  // 電柱(2)
  .group('電柱')
    .text('k2areaName',  100, '地区(2)',     READONLY)
    .text('k2lineName',  100, '冠称(2)',     READONLY)
    .text('k2poleNo',    120, '電柱番号(2)', READONLY)
    .text('k2poleKind',  100, '施設名(2)',   READONLY)
    .end()

  // 径間
  .group('')
    .text('bukkenNo',     64, '物件\n番号', READONLY)
    .list('idoType',     100, '異動種別')
    .list('setubiCode',  160, '架設設備', REQUIRED)
    .numb('keikan',      100, '径間(m)', { format: 'n1' })
    .numb('tido',        100, '弛度(m)', { format: 'n2' })
    .numb('rikakuTsn',   120, KKN_RIKAKU + '通信線(m)', { format: 'n1' })
    .numb('rikakuHkk',   120, KKN_RIKAKU + '引込線(m)', { format: 'n1' })
    .list('senkaJyokyo', 100, '線下状況')
    .end()
;

// グリッド属性
const GRID_PROPS = {
  headersVisibility: 'All',      // 行ヘッダー表示
  rowHeaderType: 'edit',         // 編集ボタン有効
  DeleteOn: true,                // 削除ボタン有効
  filterOn: true,                // フィルタ有効
  allowSorting:  'None',         // ソート不可
  frozenColumns: 6,              // 列固定
  style: { maxHeight: '50vh' },  // 最大高

  // フィルタ有効列
  filters: [
    'k1areaName', 'k1lineName', 'k1poleNo', 'k1poleKind',
    'k2areaName', 'k2lineName', 'k2poleNo', 'k2poleKind',
    'setubiCode', 'idoType', 'keikan', 'tido',
    'senkaJyokyo', 'rikakuKeikan',
  ],
};

/**
 * 径間一覧
 */
export default function KeikanListGrid({
  gridRef,
  devCodeMap,
  nearDevMap,
  idoTypeMap,
  belowStatMap,
  isReadOnly,
  isVisible,
  onItemEdit,
  onItemSelect,
  onItemUpdate,
}) {

  // 初期化処理
  const handleInitialize = React.useCallback((flexGrid) => {
    flexGrid.hostElement.addEventListener('click', (e) => {
      const hit = flexGrid.hitTest(e);

      // 設備選択 (行頭セルクリック)
      if (hit.cellType === CellType.RowHeader) {
        const item = flexGrid.rows[hit.row].dataItem;
        onItemSelect(item);
      }
    });
  }, [onItemSelect]);

  // 一覧更新処理
  const handleSourceChange = React.useCallback((s, e) => {
    s.collectionView?.collectionChanged?.addHandler((cv, ev) => {
      onItemUpdate(cv.sourceCollection);
    });
  }, [onItemUpdate]);

  // 編集ボタン処理
  const handleItemEdit = React.useCallback((s, e, keikan) => {
    onItemEdit(keikan);
  }, [onItemEdit]);

  // 一覧項目設定
  const gridView = React.useMemo(() => GRID_COLUMNS.createMultiRow({

    // プルダウン設定
    setubiCode:  { dataMap: devCodeMap },    // 架設設備コード
    nearSetubi:  { dataMap: nearDevMap },    // 再接近配電設備
    idoType:     { dataMap: idoTypeMap },    // 異動種別
    senkaJyokyo: { dataMap: belowStatMap },  // 線下状況
  }), [devCodeMap, nearDevMap, idoTypeMap, belowStatMap]);

  // フェードイン表示 (ちらつき回避)
  const [opacity, setOpacity] = React.useState(0);
  React.useEffect(() => setOpacity(isVisible ? 1 : 0), [isVisible]);

  //
  // 径間一覧描画
  //
  return (
    <div hidden={!isVisible}  // フェードイン表示
      style={{ opacity: opacity, transition: 'opacity 0.5s ease-in-out' }}
    >
      <CustomMultiRow
        ref={gridRef}
        {...GRID_PROPS}
        isReadOnly={isReadOnly}
        editPopupItems={handleItemEdit}
        initializedFunction={handleInitialize}
        itemsSourceChangedFunction={handleSourceChange}
      >
        {gridView}
      </CustomMultiRow>
    </div>
  );
}

// プロパティ
KeikanListGrid.propTypes = {
  devCodeMap: PropTypes.object,             // 架設設備コードマップ
  nearDevMap: PropTypes.object,             // 最接近配電設備マップ
  idoTypeMap: PropTypes.object,             // 異動種別マップ
  isReadOnly: PropTypes.bool.isRequired,    // 保存可否
  isVisible:  PropTypes.bool.isRequired,    // 表示切替

  // イベント処理
  onItemEdit:   PropTypes.func.isRequired,    // 設備編集
  onItemSelect: PropTypes.func.isRequired,    // 設備選択
  onItemUpdate: PropTypes.func.isRequired,    // 設備更新
};
